// src/components/InstallationInvoiceView.jsx
import React from 'react';
import { X, Printer, Pencil } from 'lucide-react';

// Reusable invoice content (used in both modal and print container)
const InvoiceContent = ({ invoice, totalPayable, monthlyPrice, grandTotal, qrCodes }) => (
  <div className="px-5 py-4">
    {/* Header */}
    <div className="flex items-center justify-between border-b-2 border-purple-600 pb-3 mb-4">
      <div>
        <h1 className="text-xl font-bold text-purple-700 leading-tight">OPTIC FLOW</h1>
        <p className="text-xs text-gray-500">Internet Services</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-gray-500 font-semibold">Invoice #{invoice.id}</p>
        <p className="text-xs text-gray-500">
          {new Date(invoice.created_at).toLocaleDateString('en-PK', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
          invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
          invoice.status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
          'bg-red-100 text-red-700'
        }`}>
          {invoice.status?.toUpperCase() || 'PENDING'}
        </span>
      </div>
    </div>

    {/* Customer Details */}
    <div className="mb-4">
      <h3 className="text-xs font-bold text-gray-500 uppercase mb-2 ">Customer Details</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <div><span className="text-gray-800 font-bold">Name:</span> <span className="font-medium">{invoice.customer_name}</span></div>
        <div><span className="text-gray-800 font-bold">Username:</span> <span className="font-medium">{invoice.customer_username}</span></div>
        <div><span className="text-gray-800 font-bold">Phone:</span> <span className="font-medium">{invoice.customer_phone || 'N/A'}</span></div>
        {invoice.package_name && (
          <div><span className="text-gray-800 font-bold">Package:</span> <span className="font-medium">{invoice.package_name}</span></div>
        )}
      </div>
    </div>

    {/* Installation Items */}
    <div className="mb-4">
      <h3 className="text-xs font-bold text-gray-500 uppercase mb-2">Installation Charges</h3>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-90">
            <th className="text-left py-2 px-3 border font-medium font-bold text-gray-600">Description</th>
            <th className="text-right py-2 px-3 border font-medium font-bold text-gray-600">Actual</th>
            <th className="text-right py-2 px-3 border font-medium font-bold text-gray-600">Discount</th>
            <th className="text-right py-2 px-3 border font-medium font-bold text-gray-600">Payable</th>
            <th className="text-center py-2 px-3 border font-medium font-bold text-gray-600">Owner</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items?.map((item, i) => (
            <tr key={i}>
              <td className="py-2 px-3 border">{item.description}</td>
              <td className="py-2 px-3 border text-right">{parseFloat(item.actual_amount || 0).toLocaleString()}</td>
              <td className="py-2 px-3 border text-right">{parseFloat(item.discount_amount || 0).toLocaleString()}</td>
              <td className="py-2 px-3 border text-right font-medium">{parseFloat(item.payable_amount || 0).toLocaleString()}</td>
              <td className="py-2 px-3 border text-center">
                <span className={`px-2 py-0.5 rounded text-[10px] ${
                  item.ownership === 'company' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {item.ownership === 'company' ? 'Company' : 'User'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Ownership */}
    <div className="mb-4 grid grid-cols-2 gap-3">
      {invoice.items?.filter(i => i.ownership === 'company').length > 0 && (
        <div className="bg-blue-50 rounded p-2">
          <h4 className="text-[10px] font-bold text-blue-700 uppercase">Company Owned</h4>
          <p className="text-xs text-blue-900">{invoice.items.filter(i => i.ownership === 'company').map(i => i.description).join(', ')}</p>
        </div>
      )}
      {invoice.items?.filter(i => i.ownership === 'user').length > 0 && (
        <div className="bg-green-50 rounded p-2">
          <h4 className="text-[10px] font-bold text-green-700 uppercase">Customer Owned</h4>
          <p className="text-xs text-green-900">{invoice.items.filter(i => i.ownership === 'user').map(i => i.description).join(', ')}</p>
        </div>
      )}
    </div>

    {/* Totals */}
    <div className="mb-4">
      <div className="bg-gray-50 rounded p-3 space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 font-bold">Installation Total:</span>
          <span className="font-medium">Rs. {totalPayable.toLocaleString()}</span>
        </div>
        {monthlyPrice > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 font-bold">Monthly Package ({invoice.package_name}):</span>
            <span className="font-medium">Rs. {monthlyPrice.toLocaleString()}</span>
          </div>
        )}
        <div className="border-t pt-1.5 flex justify-between">
          <span className="font-bold text-gray-800 text-sm">Grand Total (First Payment):</span>
          <span className="text-base font-bold text-purple-700">Rs. {grandTotal.toLocaleString()}</span>
        </div>
      </div>
    </div>

    {/* Payment QR Codes */}
    {qrCodes && Object.keys(qrCodes).length > 0 && (
      <div className="mb-4">
        <h3 className="text-xs font-bold text-gray-500 uppercase mb-2">Scan to Pay — Rs. {grandTotal.toLocaleString()}</h3>
        <div className="grid grid-cols-3 gap-3">
          {qrCodes.jazzcash && (
            <div className="border rounded-lg p-2.5 text-center">
              <img src={qrCodes.jazzcash} alt="JazzCash QR" className="w-20 h-20 mx-auto" />
              <p className="text-xs font-bold text-orange-600 mt-1.5">JazzCash</p>
            </div>
          )}
          {qrCodes.easypaisa && (
            <div className="border rounded-lg p-2.5 text-center">
              <img src={qrCodes.easypaisa} alt="EasyPaisa QR" className="w-20 h-20 mx-auto" />
              <p className="text-xs font-bold text-green-600 mt-1.5">EasyPaisa</p>
            </div>
          )}
          {qrCodes.bank && (
            <div className="border rounded-lg p-2.5 text-center">
              <img src={qrCodes.bank} alt="Bank QR" className="w-20 h-20 mx-auto" />
              <p className="text-xs font-bold text-blue-600 mt-1.5">Allied Bank</p>
            </div>
          )}
        </div>
        <p className="text-[14px] text-gray-800 text-center mt-2">
          Scan with your banking app. Amount &amp; reference will auto-fill.
        </p>
      </div>
    )}

    {/* Paid Amount (when paid or partial) */}
    {(invoice.status === 'paid' || invoice.status === 'partial') && (
      <div className="mb-4">
        <div className={`rounded p-3 ${invoice.status === 'paid' ? 'bg-green-50' : 'bg-yellow-50'}`}>
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-600">Paid Amount:</span>
            <span className={`font-bold text-base ${invoice.status === 'paid' ? 'text-green-700' : 'text-yellow-700'}`}>
              Rs. {parseFloat(invoice.paid_amount || 0).toLocaleString()}
            </span>
          </div>
          {invoice.status === 'partial' && (
            <div className="flex justify-between text-sm mt-1">
              <span className="font-medium text-gray-600">Remaining:</span>
              <span className="font-bold text-red-600">Rs. {(grandTotal - parseFloat(invoice.paid_amount || 0)).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Footer */}
    <div className="border-t-2 border-purple-600 pt-2 text-center text-[14px] text-gray-800">
      <p className={invoice.status === 'paid' ? 'text-green-600 font-semibold' : ''}>
        {invoice.status === 'paid'
          ? 'Payment received. Thank you!'
          : invoice.status === 'partial'
            ? 'Partial payment received. Remaining balance due.'
            : 'Service will be activated upon full payment. Send payment screenshot to confirm.'}
      </p>
      <p className="mt-1 font-medium text-gray-500">&copy; {new Date().getFullYear()} Optic Flow Internet Services</p>
    </div>
  </div>
);

// Build plain HTML string for the print window (not React — actual HTML)
function buildPrintHTML(invoice, totalPayable, monthlyPrice, grandTotal, qrCodes) {
  const statusClass = invoice.status === 'paid' ? 'badge-green' : invoice.status === 'partial' ? 'badge-yellow' : 'badge-red';
  const statusText = invoice.status?.toUpperCase() || 'PENDING';
  const date = new Date(invoice.created_at).toLocaleDateString('en-PK', { year: 'numeric', month: 'long', day: 'numeric' });

  const itemsHTML = (invoice.items || []).map(item => {
    const owner = item.ownership === 'company' ? 'Company' : 'User';
    return `<tr>
      <td>${item.description}</td>
      <td style="text-align:right">${parseFloat(item.actual_amount || 0).toLocaleString()}</td>
      <td style="text-align:right">${parseFloat(item.discount_amount || 0).toLocaleString()}</td>
      <td style="text-align:right;font-weight:500">${parseFloat(item.payable_amount || 0).toLocaleString()}</td>
      <td style="text-align:center">${owner}</td>
    </tr>`;
  }).join('');

  const companyItems = (invoice.items || []).filter(i => i.ownership === 'company').map(i => i.description);
  const userItems = (invoice.items || []).filter(i => i.ownership === 'user').map(i => i.description);
  const ownershipHTML = (companyItems.length > 0 || userItems.length > 0) ? `
    <div class="ownership">
      ${companyItems.length > 0 ? `<div class="own-box company"><h4>Company Owned</h4><p>${companyItems.join(', ')}</p></div>` : ''}
      ${userItems.length > 0 ? `<div class="own-box user"><h4>Customer Owned</h4><p>${userItems.join(', ')}</p></div>` : ''}
    </div>
  ` : '';

  const qrHTML = qrCodes && Object.keys(qrCodes).length > 0 ? `
    <div class="qr-section">
      <h3>Scan to Pay — Rs. ${grandTotal.toLocaleString()}</h3>
      <div class="qr-grid">
        ${qrCodes.jazzcash ? `<div class="qr-card"><img src="${qrCodes.jazzcash}" alt="JazzCash" /><p class="jc">JazzCash</p></div>` : ''}
        ${qrCodes.easypaisa ? `<div class="qr-card"><img src="${qrCodes.easypaisa}" alt="EasyPaisa" /><p class="ep">EasyPaisa</p></div>` : ''}
        ${qrCodes.bank ? `<div class="qr-card"><img src="${qrCodes.bank}" alt="Bank" /><p class="bk">Allied Bank</p></div>` : ''}
      </div>
      <p class="qr-hint">Scan with your banking app. Amount &amp; reference will auto-fill.</p>
    </div>
  ` : '';

  const paidAmount = parseFloat(invoice.paid_amount || 0);
  const remaining = grandTotal - paidAmount;
  const paidHTML = (invoice.status === 'paid' || invoice.status === 'partial') ? `
    <div class="totals" style="background: ${invoice.status === 'paid' ? '#f0fdf4' : '#fefce8'}">
      <div class="row"><span class="label">Paid Amount:</span><span style="font-weight:700;color:${invoice.status === 'paid' ? '#166534' : '#854d0e'}">Rs. ${paidAmount.toLocaleString()}</span></div>
      ${invoice.status === 'partial' ? `<div class="row"><span class="label">Remaining:</span><span style="font-weight:700;color:#991b1b">Rs. ${remaining.toLocaleString()}</span></div>` : ''}
    </div>
  ` : '';
  const footerText = invoice.status === 'paid'
    ? 'Payment received. Thank you!'
    : invoice.status === 'partial'
      ? 'Partial payment received. Remaining balance due.'
      : 'Service will be activated upon full payment. Send payment screenshot to confirm.';

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Invoice #${invoice.id}</title>
<style>
  @page { size: A4 portrait; margin: 10mm 15mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, sans-serif; color: #333; font-size: 13px; }
  .invoice { max-width: 480px; margin: 0 auto; padding: 16px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #7c3aed; padding-bottom: 12px; margin-bottom: 16px; }
  .header h1 { font-size: 20px; color: #7c3aed; }
  .header p { font-size: 12px; color: #6b7280; }
  .header .right { text-align: right; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 600; margin-top: 4px; }
  .badge-green { background: #dcfce7; color: #166534; }
  .badge-yellow { background: #fef9c3; color: #854d0e; }
  .badge-red { background: #fee2e2; color: #991b1b; }
  h3 { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 8px; letter-spacing: 0.5px; }
  .details { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; font-size: 13px; margin-bottom: 16px; }
  .details .label { color: #1f2937; font-weight: 700; }
  .details .value { font-weight: 500; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 12px; }
  th { background: #f9fafb; font-weight: 700; color: #4b5563; text-align: left; padding: 8px 12px; border: 1px solid #e5e7eb; }
  td { padding: 8px 12px; border: 1px solid #e5e7eb; }
  .ownership { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
  .own-box { border-radius: 4px; padding: 8px; }
  .own-box h4 { font-size: 10px; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
  .own-box p { font-size: 12px; }
  .own-box.company { background: #eff6ff; }
  .own-box.company h4 { color: #1d4ed8; }
  .own-box.company p { color: #1e3a8a; }
  .own-box.user { background: #f0fdf4; }
  .own-box.user h4 { color: #15803d; }
  .own-box.user p { color: #14532d; }
  .totals { background: #f9fafb; border-radius: 6px; padding: 12px; margin-bottom: 16px; }
  .totals .row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; }
  .totals .label { font-weight: 700; color: #6b7280; }
  .totals .total-row { border-top: 1px solid #e5e7eb; padding-top: 8px; margin-top: 4px; }
  .totals .total-row .amount { font-size: 16px; font-weight: 700; color: #7c3aed; }
  .qr-section { margin-bottom: 16px; }
  .qr-section h3 { text-align: center; }
  .qr-grid { display: flex; justify-content: center; gap: 12px; }
  .qr-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; text-align: center; }
  .qr-card img { width: 80px; height: 80px; display: block; margin: 0 auto; }
  .qr-card p { font-size: 11px; font-weight: 700; margin-top: 6px; }
  .qr-card .jc { color: #ea580c; }
  .qr-card .ep { color: #16a34a; }
  .qr-card .bk { color: #2563eb; }
  .qr-hint { font-size: 14px; color: #1f2937; text-align: center; margin-top: 8px; }
  .footer { border-top: 2px solid #7c3aed; padding-top: 8px; text-align: center; font-size: 13px; color: #1f2937; }
  .footer .copy { margin-top: 4px; font-weight: 500; color: #6b7280; }
</style></head><body>
<div class="invoice">
  <div class="header">
    <div><h1>OPTIC FLOW</h1><p>Internet Services</p></div>
    <div class="right"><p>Invoice #${invoice.id}</p><p>${date}</p><span class="badge ${statusClass}">${statusText}</span></div>
  </div>
  <h3>Customer Details</h3>
  <div class="details">
    <div><span class="label">Name:</span> <span class="value">${invoice.customer_name}</span></div>
    <div><span class="label">Username:</span> <span class="value">${invoice.customer_username}</span></div>
    <div><span class="label">Phone:</span> <span class="value">${invoice.customer_phone || 'N/A'}</span></div>
    ${invoice.package_name ? `<div><span class="label">Package:</span> <span class="value">${invoice.package_name}</span></div>` : ''}
  </div>
  <h3>Installation Charges</h3>
  <table>
    <thead><tr><th>Description</th><th style="text-align:right">Actual</th><th style="text-align:right">Discount</th><th style="text-align:right">Payable</th><th style="text-align:center">Owner</th></tr></thead>
    <tbody>${itemsHTML}</tbody>
  </table>
  ${ownershipHTML}
  <div class="totals">
    <div class="row"><span class="label">Installation Total:</span><span>Rs. ${totalPayable.toLocaleString()}</span></div>
    ${monthlyPrice > 0 ? `<div class="row"><span class="label">Monthly Package (${invoice.package_name}):</span><span>Rs. ${monthlyPrice.toLocaleString()}</span></div>` : ''}
    <div class="row total-row"><span class="label" style="font-size:13px">Grand Total (First Payment):</span><span class="amount">Rs. ${grandTotal.toLocaleString()}</span></div>
  </div>
  ${qrHTML}
  ${paidHTML}
  <div class="footer"><p${invoice.status === 'paid' ? ' style="color:#166534;font-weight:600"' : ''}>${footerText}</p><p class="copy">&copy; ${new Date().getFullYear()} Optic Flow Internet Services</p></div>
</div>
<script>window.onload=function(){window.print();};</script>
</body></html>`;
}

const InstallationInvoiceView = ({ invoice, onClose, onEdit }) => {
  if (!invoice) return null;

  const totalPayable = parseFloat(invoice.total_payable) || invoice.items?.reduce((sum, item) => sum + parseFloat(item.payable_amount || 0), 0) || 0;
  const monthlyPrice = parseFloat(invoice.monthly_price || 0);
  const grandTotal = totalPayable + monthlyPrice;

  // QR codes come from the serializer — no extra API call needed
  const qrCodes = invoice.payment_qr_codes || null;

  const handlePrint = () => {
    const html = buildPrintHTML(invoice, totalPayable, monthlyPrice, grandTotal, qrCodes);
    const win = window.open('', '_blank', 'width=800,height=1000');
    if (win) {
      win.document.write(html);
      win.document.close();
    } else {
      alert('Please allow pop-ups to print the invoice.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[480px] max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-2.5 flex items-center justify-between rounded-t-xl z-10">
          <h2 className="text-sm font-bold text-gray-900">Installation Invoice</h2>
          <div className="flex items-center gap-2">
            {onEdit && invoice.status !== 'paid' && (
              <button onClick={() => onEdit(invoice)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs rounded-lg hover:bg-amber-100">
                <Pencil size={13} /> Edit
              </button>
            )}
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700">
              <Printer size={13} /> Print
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full">
              <X size={16} />
            </button>
          </div>
        </div>
        <InvoiceContent invoice={invoice} totalPayable={totalPayable} monthlyPrice={monthlyPrice} grandTotal={grandTotal} qrCodes={qrCodes} />
      </div>
    </div>
  );
};

export default InstallationInvoiceView;
